import { useI18n } from 'modules/i18n';
import { useProject } from 'modules/routes';

import { getProjectLabel } from '../utils';
import { useBreadcrumbModel } from './useBreadcrumbModel';

/**
 * Returns route breadcrumbs with the localized project display name.
 * Hides items until the current project's details are available.
 *
 * @returns {Object[]} Breadcrumb items with labels, optional links, and loading flags.
 */
export function useBreadcrumbs() {
    const { locale } = useI18n();
    const { project } = useProject();
    const { currentPage, items } = useBreadcrumbModel();

    if (!project) {
        return [];
    }

    const projectLabel = project.is_umbrella
        ? null
        : getProjectLabel(project, locale);

    if (currentPage.pageType === 'project_startpage') {
        return projectLabel
            ? [{ key: 'archive', label: projectLabel, isProjectRoot: true }]
            : [];
    }

    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    // Map model items to breadcrumb items, ensuring `to` is only included when defined
    const crumbs = items.map((item) => ({
        key: item.key,
        label: item.label,
        to: item.to || undefined,
        isProjectRoot: item.key === 'archive',
        ...(item.loading ? { loading: true } : {}),
    }));

    // Add project root as first breadcrumb item for non-umbrella projects
    const shouldPrependProjectRoot =
        projectLabel && crumbs.length > 0 && crumbs[0]?.isProjectRoot !== true;

    if (shouldPrependProjectRoot) {
        return [
            {
                key: 'archive',
                label: projectLabel,
                to: currentPage.pathBase || undefined,
                isProjectRoot: true,
            },
            ...crumbs,
        ];
    }

    return crumbs;
}

export default useBreadcrumbs;
